'use client';

import React, { useEffect, useState } from 'react';

const STAGES = [
  { label: 'Initializing secure connection…', progress: 15 },
  { label: 'Loading intelligence modules…', progress: 38 },
  { label: 'Syncing revenue data streams…', progress: 58 },
  { label: 'Calibrating AI fraud detection…', progress: 76 },
  { label: 'Establishing terminal feeds…', progress: 90 },
  { label: 'System ready.', progress: 100 },
];

export default function SiteLoader() {
  const [stageIndex, setStageIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    let idx = 0;
    const advance = () => {
      idx += 1;
      if (idx < STAGES?.length) {
        setStageIndex(idx);
        const delay = idx === STAGES?.length - 1 ? 500 : 420 + Math.random() * 180;
        setTimeout(advance, delay);
      } else {
        // Begin fade-out
        setTimeout(() => {
          setFadeOut(true);
          setTimeout(() => setVisible(false), 600);
        }, 400);
      }
    };
    const timer = setTimeout(advance, 350);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  const { label, progress } = STAGES?.[stageIndex];

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        background: '#0A0F1E',
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 600ms ease',
        pointerEvents: fadeOut ? 'none' : 'all',
      }}
    >
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(14,165,233,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      {/* Ambient glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(14,165,233,0.10) 0%, transparent 70%)',
          filter: 'blur(60px)',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
      {/* Content */}
      <div className="relative flex flex-col items-center gap-8 w-full max-w-sm px-8">

        {/* Logo mark */}
        <div className="flex flex-col items-center gap-3">
          {/* Animated hex icon */}
          <div className="relative flex items-center justify-center" style={{ width: 72, height: 72 }}>
            {/* Outer ring */}
            <div
              className="absolute inset-0 rounded-full border border-sky-500/20"
              style={{ animation: 'loaderSpin 8s linear infinite' }}
            />
            {/* Middle ring */}
            <div
              className="absolute rounded-full border border-sky-400/30"
              style={{
                inset: 8,
                animation: 'loaderSpinReverse 5s linear infinite',
                borderTopColor: 'rgba(14,165,233,0.7)',
              }}
            />
            {/* Core */}
            <div
              className="relative flex items-center justify-center rounded-full"
              style={{
                width: 44,
                height: 44,
                background: 'linear-gradient(135deg, #0EA5E9 0%, #0369A1 100%)',
                boxShadow: '0 0 24px rgba(14,165,233,0.45)',
              }}
            >
              {/* P letter mark */}
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <path
                  d="M6 4h6a4 4 0 0 1 0 8H6V4z"
                  fill="white"
                  fillOpacity="0.95"
                />
                <path d="M6 12v6" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </div>
          </div>

          {/* Brand name */}
          <div className="flex flex-col items-center gap-0.5">
            <span
              className="text-2xl font-bold tracking-widest"
              style={{ color: '#E2E8F0', letterSpacing: '0.22em' }}
            >
              PETRIX
            </span>
            <span
              className="text-[10px] font-medium tracking-widest uppercase"
              style={{ color: '#0EA5E9', letterSpacing: '0.18em' }}
            >
              Revenue Intelligence
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full flex flex-col gap-2">
          <div
            className="w-full rounded-full overflow-hidden"
            style={{ height: 3, background: 'rgba(14,165,233,0.12)' }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #0369A1, #0EA5E9, #38BDF8)',
                borderRadius: 9999,
                transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 0 8px rgba(14,165,233,0.6)',
              }}
            />
          </div>

          {/* Status row */}
          <div className="flex items-center justify-between">
            <span
              className="text-xs font-medium"
              style={{
                color: '#64748B',
                fontFamily: 'var(--font-geist-mono, monospace)',
                transition: 'opacity 300ms',
              }}
            >
              {label}
            </span>
            <span
              className="text-xs tabular-nums"
              style={{
                color: '#0EA5E9',
                fontFamily: 'var(--font-geist-mono, monospace)',
              }}
            >
              {progress}%
            </span>
          </div>
        </div>

        {/* Scanning dots */}
        <div className="flex items-center gap-1.5">
          {[0, 1, 2, 3]?.map((i) => (
            <div
              key={i}
              style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: '#0EA5E9',
                animation: `loaderDot 1.4s ease-in-out ${i * 0.18}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
      {/* Bottom classification bar */}
      <div
        className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-3"
        style={{ borderTop: '1px solid rgba(14,165,233,0.08)' }}
      >
        <span
          className="text-[10px] tracking-widest uppercase"
          style={{ color: '#334155', letterSpacing: '0.2em' }}
        >
          Liberia Revenue Authority · Classified System · Authorized Access Only
        </span>
      </div>
      <style>{`
        @keyframes loaderSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes loaderSpinReverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes loaderDot {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
}
